/**
 * `bootRefresh()` runs at cold load and on `/auth/return`: on success it stores the bearer + CSRF
 * token; on failure the caller redirects to centralized login. The deep link (PATH only, never a
 * token) survives the login round trip in sessionStorage. `loginUrl` is the bootstrapped platform
 * singleton used VERBATIM — it carries no query string, so `?returnUrl=` is always appended.
 */
import { authApi } from '@/api/auth';
import { getBootstrap } from '@/config/bootstrap';
import { env } from '@/config/env';
import { setAccessToken, setCsrfToken } from './authStore';

const DEEP_LINK_KEY = 'tmm.deepLink';
export const AUTH_RETURN_PATH = '/auth/return';

export async function bootRefresh(): Promise<boolean> {
  try {
    const body = await authApi.refresh();
    if (!body.accessToken) return false;
    setAccessToken(body.accessToken, body.expiresAt ?? null);
    setCsrfToken(body.csrfToken ?? null);
    return true;
  } catch {
    return false; // 401 / 403 / network all mean "not authenticated" at boot
  }
}

export function stashDeepLink(path: string): void {
  if (path === '/' || path.startsWith('/auth/')) return; // never a loop path
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* storage unavailable */
  }
}

export function popDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return v;
  } catch {
    return null;
  }
}

export function centralizedLoginUrl(returnUrl: string): string {
  return `${getBootstrap().loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** Stash the current path, then leave for centralized login; it returns to `/auth/return`. */
export function redirectToCentralizedLogin(currentPath: string): void {
  stashDeepLink(currentPath);
  window.location.assign(centralizedLoginUrl(`${env.appOrigin}${AUTH_RETURN_PATH}`));
}
